import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { message, Spin } from 'antd';
import { PiNotePencilBold } from 'react-icons/pi';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { CollectFormTemplateAPIs } from '~/common/typescript/apis/CollectFormTemplateAPIs';
import { CollectFormTemplate } from '~/common/typescript/models/CollectFormTemplate';
import Routes from '~/routes/routes';
import { UI_ScreenStyles } from '../UI_ScreenStyles';
import { createBreadcrumbItems, commonBreadcrumbs } from '~/utils/breadcrumbUtils';
import { CollectFormTemplateHeader } from './components/CollectFormTemplateHeader';
import { CollectFormTemplateFilters } from './components/CollectFormTemplateFilters';
import { CollectFormTemplateGrid } from './components/CollectFormTemplateGrid';
import { CollectFormTemplateList } from './components/CollectFormTemplateList';

export const CollectFormTemplateScreen: React.FC = () => {
  const navigate = useNavigate();

  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [templates, setTemplates] = useState<CollectFormTemplate[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  // Filters & search
  const [searchText, setSearchText] = useState<string>('');
  const [sqliteFilter, setSqliteFilter] = useState<'all' | 'has_sqlite' | 'no_sqlite'>('all');
  const [sortBy, setSortBy] = useState<'date_desc' | 'date_asc' | 'name_asc' | 'name_desc'>('date_desc');

  // Pagination
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(12);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const data = await CollectFormTemplateAPIs.remote_getTemplates();
      setTemplates(data || []);
    } catch (error) {
      console.error('[CollectFormTemplateScreen] Failed to load templates:', error);
      message.error('Không thể tải danh sách mẫu biểu mẫu');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (uuid: string) => {
    try {
      await CollectFormTemplateAPIs.remote_deleteTemplate(uuid);
      message.success('Xoá mẫu biểu mẫu thành công');
      loadTemplates();
    } catch (error) {
      console.error('[CollectFormTemplateScreen] Failed to delete template:', error);
      message.error('Không thể xoá mẫu biểu mẫu');
    }
  };

  const handleExportZip = async (template: CollectFormTemplate) => {
    try {
      const zip = new JSZip();
      const timestamp = Date.now();

      // 1. Add manifest.json
      const manifest = {
        version: '2.0.0',
        exportedAt: new Date().toISOString(),
        exportedBy: 'geocloud-web (client-side)',
        formName: template.name,
      };
      zip.file('manifest.json', JSON.stringify(manifest, null, 2));

      // 2. Add form.json
      const formJson = {
        uuid: template.uuid,
        name: template.name,
        description: template.description || '',
        form_version: 2,
        content: template.content,
      };
      zip.file('form.json', JSON.stringify(formJson, null, 2));

      const content = await zip.generateAsync({ type: 'blob' });
      const sanitizedName = template.name.replace(/[^a-z0-9]/gi, '-').toLowerCase();
      const filename = `form-${sanitizedName}-${timestamp}.zip`;
      saveAs(content, filename);

      message.success(`Đã xuất mẫu biểu mẫu "${template.name}" thành công!`);
    } catch (err) {
      console.error('Export zip failed:', err);
      message.error('Xuất gói ZIP thất bại');
    }
  };

  const handleDesignNew = () => {
    navigate(Routes.COLLECT_FORM_TEMPLATE_DESIGN || Routes.COLLECT_FORM_DESIGN);
  };

  const handleEdit = (form: CollectFormTemplate) => {
    navigate(`/collect-form-template/design/${form.uuid}`);
  };

  // Filter and sort items
  const filteredTemplates = useMemo(() => {
    let result = [...templates];

    // Search filter
    if (searchText.trim()) {
      const query = searchText.toLowerCase().trim();
      result = result.filter(
        (t) =>
          t.name.toLowerCase().includes(query) ||
          t.description?.toLowerCase().includes(query),
      );
    }

    // SQLite filter
    if (sqliteFilter === 'has_sqlite') {
      result = result.filter((t) => Boolean(t.sqlite_files));
    } else if (sqliteFilter === 'no_sqlite') {
      result = result.filter((t) => !t.sqlite_files);
    }

    // Sort
    result.sort((a, b) => {
      if (sortBy === 'date_asc') {
        return (a.created_at || '').localeCompare(b.created_at || '');
      }
      if (sortBy === 'name_asc') {
        return a.name.localeCompare(b.name);
      }
      if (sortBy === 'name_desc') {
        return b.name.localeCompare(a.name);
      }
      // default: date_desc
      return (b.created_at || '').localeCompare(a.created_at || '');
    });

    return result;
  }, [templates, searchText, sqliteFilter, sortBy]);

  // Paginated slice
  const paginatedTemplates = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredTemplates.slice(start, start + pageSize);
  }, [filteredTemplates, currentPage, pageSize]);

  const handlePageChange = (page: number, newPageSize: number) => {
    setCurrentPage(page);
    setPageSize(newPageSize);
  };

  return (
    <UI_ScreenStyles.UI_Wrapper>
      <UI_ScreenStyles.UI_Breadcrumb
        items={createBreadcrumbItems([
          commonBreadcrumbs.home,
          {
            title: 'Template biểu mẫu',
            icon: <PiNotePencilBold style={{ fontSize: '16px' }} />,
          },
        ])}
      />

      <UI_ScreenContainer>
        <CollectFormTemplateHeader
          viewMode={viewMode}
          totalCount={filteredTemplates.length}
          onViewModeChange={setViewMode}
          onDesignNew={handleDesignNew}
        />

        <CollectFormTemplateFilters
          searchText={searchText}
          onSearchChange={(val) => {
            setSearchText(val);
            setCurrentPage(1);
          }}
          sqliteFilter={sqliteFilter}
          onSqliteFilterChange={(val) => {
            setSqliteFilter(val);
            setCurrentPage(1);
          }}
          sortBy={sortBy}
          onSortByChange={setSortBy}
        />

        <UI_ContentArea>
          {loading && templates.length === 0 ? (
            <UI_LoadingWrapper>
              <Spin size="large" tip="Đang tải danh sách template biểu mẫu..." />
            </UI_LoadingWrapper>
          ) : (
            <>
              {viewMode === 'grid' ? (
                <CollectFormTemplateGrid
                  forms={paginatedTemplates}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onExportZip={handleExportZip}
                  currentPage={currentPage}
                  pageSize={pageSize}
                  totalCount={filteredTemplates.length}
                  onPageChange={handlePageChange}
                />
              ) : (
                <CollectFormTemplateList
                  forms={paginatedTemplates}
                  loading={loading}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onExportZip={handleExportZip}
                  currentPage={currentPage}
                  pageSize={pageSize}
                  totalCount={filteredTemplates.length}
                  onPageChange={handlePageChange}
                />
              )}
            </>
          )}
        </UI_ContentArea>
      </UI_ScreenContainer>
    </UI_ScreenStyles.UI_Wrapper>
  );
};

export default CollectFormTemplateScreen;

const UI_ScreenContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: #ffffff;
  overflow-y: auto;
`;

const UI_ContentArea = styled.div`
  flex: 1;
`;

const UI_LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 360px;
`;
